import { faEllipsisVertical, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { inMarketTheme } from '@inmarket/inmarkit';
import {
  ClickAwayListener,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';

import CoreKpiIcon from '../../assets/core-kpi-icon.svg';
import {
  CONVERSION_KPI_OPTION,
  CONVERSION_SCORED_LIST_WARNING,
  POPULATION_DENSITY_PACING_SEED_TYPE_LABEL,
  SECONDARY_KPI_OPTIONS,
} from '../../constants/lineItems';
import useConfirm from '../../hooks/useConfirm';
import { useConstants } from '../../hooks/useConstants';
import { LineItem } from '../../validationSchemas/LineItemSchema';
import AutocompleteLocationField from '../AdGroups/TargetingLocations/AutocompleteLocationField';
import AdGroupSelector from '../forms/AdGroupSelector';
import KpiProductCategorySelector from '../forms/KpiProductCategorySelector';
import KpiProductSelector from '../forms/KpiProductSelector';
import NumericTextField from '../forms/NumericTextField';
import TextField from '../forms/TextField';

function LineItemKpiRow({
  index,
  remove,
  append,
  enabledKpis,
  totalKpis,
}: {
  index: number;
  remove: Function;
  append: Function;
  enabledKpis: string[];
  totalKpis: number;
}) {
  const { constants } = useConstants();
  const CORE_KPI_SEED_TYPES = (constants?.INSERTION_ORDER?.SEED_TYPES || [])
    .map(([, label]) => ({
      value: label,
      label,
      type: 'coreKpi',
    }))
    .filter((item) => item.label !== POPULATION_DENSITY_PACING_SEED_TYPE_LABEL);

  const setValueOptions = { shouldValidate: true, shouldDirty: true };
  const { getValues, setValue, watch }: UseFormReturn<LineItem> = useFormContext();
  const lineItemId = getValues('lineItemId');

  const kpiObjectives = getValues('kpiObjectives');
  const coreKpiList = kpiObjectives.filter((kpi) => kpi.type === 'coreKpi');
  const kpiType = kpiObjectives[index]?.type;
  const options = [...SECONDARY_KPI_OPTIONS, ...CORE_KPI_SEED_TYPES];

  // Read the name from form state (not local state) so that when another row's
  // change forces this row's Secondary KPI to Conversion, the Select reflects it.
  const kpiName = (watch(`kpiObjectives.${index}.name`) as string) ?? '';

  const [hoverIndex, setHoverIndex] = useState<number>();
  const [clickIndex, setClickIndex] = useState<number>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const togglePopper = (e, rowIndex) => {
    e.stopPropagation();
    setClickIndex(rowIndex);
    setAnchorEl(e.target);
  };

  const { windowDotConfirm } = useConfirm({
    title: 'Are you sure?',
    message: CONVERSION_SCORED_LIST_WARNING,
    confirmButtonText: 'Proceed',
  });

  const onChangeKpiName = async (e) => {
    const newKpiName = e.target.value;
    const selectedOption = options.find((option) => option.value === newKpiName);
    const defaultValue = selectedOption?.defaultValue ?? null;
    const type = selectedOption?.type || null;

    const hasPrimaryKpi = coreKpiList.length > 0;

    // Secondary and Primary KPIs are decoupled: a Secondary KPI (CTR/VCR/Conversion)
    // and Primary KPIs coexist. When a Primary KPI is present, conversion-based goals
    // pair best with a Conversion-optimized scored list, so overriding the Secondary
    // KPI back to CTR/VCR prompts a warning.
    if (type === 'secondaryKpi' && (newKpiName === 'CTR' || newKpiName === 'VCR') && hasPrimaryKpi) {
      if (!(await windowDotConfirm())) {
        return;
      }
    }

    setValue(`kpiObjectives.${index}.name`, newKpiName, setValueOptions);
    setValue(`kpiObjectives.${index}.value`, defaultValue, setValueOptions);
    setValue(`kpiObjectives.${index}.type`, type, setValueOptions);

    // Selecting a Primary KPI (Receipts/Visits/Pixel Conversion) forces the Secondary
    // KPI to Conversion.
    if (type === 'coreKpi') {
      // Exclude this row: it is becoming the Primary KPI, so it must not be the row we
      // flip to Conversion (avoids clobbering the Primary KPI just selected here).
      const secondaryIndex = kpiObjectives.findIndex((kpi, i) => i !== index && kpi.type === 'secondaryKpi');
      if (secondaryIndex === -1) {
        // No Secondary KPI row exists (e.g. the user deleted the default one), so create
        // one set to Conversion rather than leaving the Primary KPI without a scored list.
        append({
          name: CONVERSION_KPI_OPTION.value,
          value: CONVERSION_KPI_OPTION.defaultValue,
          type: CONVERSION_KPI_OPTION.type,
          adGroupIds: [],
        });
      } else if (kpiObjectives[secondaryIndex]?.name !== CONVERSION_KPI_OPTION.value) {
        setValue(`kpiObjectives.${secondaryIndex}.name`, CONVERSION_KPI_OPTION.value, setValueOptions);
        setValue(`kpiObjectives.${secondaryIndex}.value`, CONVERSION_KPI_OPTION.defaultValue, setValueOptions);
      }
    }
  };

  return (
    <Stack
      direction="row"
      sx={{ width: '100%', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '8px' }}
      onMouseOver={() => setHoverIndex(index)}
      onMouseOut={() => {
        if (!clickIndex) {
          setHoverIndex(undefined);
        }
      }}
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
        <Stack alignSelf="center">
          <img src={CoreKpiIcon} alt="Core KPI icon" width="32" height="32" />
        </Stack>
        <Stack sx={{ minWidth: '140px' }}>
          <Select
            variant="standard"
            size="small"
            displayEmpty
            placeholder="Select KPI"
            value={kpiName}
            fullWidth
            onChange={onChangeKpiName}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={enabledKpis.includes(option.label) || option.disabled}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {kpiName === '' && <Typography color="error">Please select a KPI</Typography>}
        </Stack>
        {kpiName !== '' && (
          <>
            {/* Conversion is scored-list-only and has no numeric goal, so hide its input. */}
            {kpiName !== CONVERSION_KPI_OPTION.value && (
              <Stack sx={{ minWidth: '30px' }}>
                <NumericTextField
                  key={`kpiObjectives.${index}.id`}
                  name={`kpiObjectives.${index}.value`}
                  label=""
                  placeholder="Add Goal"
                  fullWidth
                  allowNegative={false}
                  InputProps={{
                    endAdornment: kpiType !== 'coreKpi' && <InputAdornment position="end">%</InputAdornment>,
                  }}
                />
              </Stack>
            )}
            <Stack flexGrow={2} sx={{ width: '100%' }}>
              <Grid container spacing={2}>
                {kpiName === 'Visits' && (
                  <Grid item md={12} lg={8}>
                    <AutocompleteLocationField
                      label=""
                      placeholder="Add Store"
                      name="visitStoreList.storeList"
                      locationType="chain"
                      fullWidth
                      autoHighlight
                      useLabelAsValue
                      multiple
                    />
                  </Grid>
                )}
                {kpiName === 'Pixel Conversion' && (
                  <Grid item md={12} lg={8}>
                    <TextField fullWidth name="conversionPixelUrl.pixelUrl" label="" placeholder="Add Pixel URL" />
                  </Grid>
                )}
                {kpiName === 'Receipts' && (
                  <>
                    <Grid item md={12} lg={4}>
                      <KpiProductSelector
                        name="receiptProductList.productList"
                        label=""
                        placeholder="Add Product"
                        noOptionsText="Enter keywords to load available options"
                      />
                    </Grid>
                    <Grid item md={12} lg={4}>
                      <KpiProductCategorySelector
                        name="receiptProductList.productLowerCategories"
                        label=""
                        placeholder="Add Category"
                        noOptionsText="Enter keywords to load available options"
                      />
                    </Grid>
                  </>
                )}
                {kpiType === 'coreKpi' && (
                  <Grid item md={12} lg={4}>
                    <AdGroupSelector
                      lineItemId={lineItemId}
                      name={`kpiObjectives.${index}.adGroupIds`}
                      label=""
                      placeholder="Add Seed Ad Groups"
                    />
                  </Grid>
                )}
              </Grid>
            </Stack>
          </>
        )}
      </Stack>
      {totalKpis > 1 && (
        <Stack
          alignSelf="center"
          onClick={(e) => e.stopPropagation()}
          sx={{
            visibility: index === hoverIndex || index === clickIndex ? 'visible' : 'hidden',
            cursor: 'pointer',
          }}
        >
          <ClickAwayListener
            onClickAway={() => {
              setHoverIndex(undefined);
              setClickIndex(undefined);
              setAnchorEl(undefined);
            }}
          >
            <div>
              <Stack sx={{ color: 'gray' }}>
                <Stack px={1} onClick={(e) => togglePopper(e, index)}>
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </Stack>
                <Popper
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  placement="bottom-end"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ zIndex: 10 }}
                >
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} onClick={() => remove(clickIndex)} sx={{ cursor: 'pointer' }}>
                      <FontAwesomeIcon icon={faTrash} size="lg" color={inMarketTheme.palette.error.main} />
                      <Typography color={inMarketTheme.palette.error.main}>Delete</Typography>
                    </Stack>
                  </Paper>
                </Popper>
              </Stack>
            </div>
          </ClickAwayListener>
        </Stack>
      )}
    </Stack>
  );
}

export default LineItemKpiRow;
